import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaStar, FaShoppingCart, FaTruck, FaShieldAlt, FaUndo, FaCheck, FaArrowRight } from "react-icons/fa";
import SummaryApi from "../common";
import { useCart } from "../context/CartContext";

const ProductCart = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart(); // Assuming context also might have cartItems, but we will use API as requested
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [allImages, setAllImages] = useState([]);
  
  // State to track if item is persistently in cart
  const [isInCart, setIsInCart] = useState(false);

  // 1. Fetch Product Details
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        
        let currentProduct = null;

        if (location.state?.product) {
          currentProduct = location.state.product;
          setProduct(currentProduct);
          setupProductImages(currentProduct);
        } else if (id) {
          currentProduct = await fetchProductById(id);
        }

        // Once we have the product, check if it's in the cart
        if (currentProduct) {
          checkCartStatus(currentProduct.product_id);
        } else {
          setLoading(false);
        }

      } catch (error) {
        console.error("Error fetching product:", error);
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [location.state, id]);

  // 2. Function to check if item is already in cart using the API
  const checkCartStatus = async (productId) => {
    try {
      const response = await fetch(SummaryApi.getCartItems.url, {
        method: SummaryApi.getCartItems.method,
        headers: {
          'Authorization': SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
        // Some retrieve APIs might need a body, typically just the token header is enough for retrieval
        // If your API requires user_id in body, add it here.
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        // Check if the current product ID exists in the cart array
        // Note: Ensure data types match (string vs number)
        const exists = data.data.some(item => 
          String(item.product_id) === String(productId)
        );
        setIsInCart(exists);
      }
    } catch (error) {
      console.error("Error checking cart status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductById = async (productId) => {
    try {
      const response = await fetch(SummaryApi.productDetails.url, {
        method: SummaryApi.productDetails.method,
        headers: {
          'Authorization': SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productid: productId
        })
      });

      const data = await response.json();
      if (data.status && data.data && data.data.length > 0) {
        const productData = data.data[0];
        setProduct(productData);
        setupProductImages(productData);
        return productData;
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    }
    return null;
  };

  const setupProductImages = (productData) => {
    const images = [];
    for (let i = 1; i <= 4; i++) {
      const imgKey = `product_img${i}`;
      if (productData[imgKey]) {
        images.push(`${SummaryApi.backendDomain}/${productData[imgKey]}`);
      }
    }
    setAllImages(images);
    if (images.length > 0) {
      setSelectedImage(images[0]);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    // If already in cart, navigate to cart page instead of adding again
    if (isInCart) {
      navigate('/cart');
      return;
    }

    try {
      const result = await addToCart(product.product_id, quantity);
      
      if (result.success) {
        // Update state to reflect it's now in cart
        setIsInCart(true);
        // Optional: Trigger a re-check of cart status just to be sure
        // checkCartStatus(product.product_id);
      } else {
        if (result.redirectToLogin) {
          alert("Please login to add items to cart");
          navigate('/login');
        } else {
          alert(result.message || 'Failed to add to cart');
        }
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert('Failed to add to cart');
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    
    // If already in cart, just go there
    if (isInCart) {
      navigate('/cart');
      return;
    }

    try {
      const result = await addToCart(product.product_id, quantity);
      if (result.success) {
        navigate('/cart');
      } else {
        if (result.redirectToLogin) {
          navigate('/login');
        } else {
          alert(result.message || 'Failed to proceed');
        }
      }
    } catch (error) {
      console.error("Error in buy now:", error);
    }
  };

  const increaseQuantity = () => setQuantity(prev => prev + 1);
  const decreaseQuantity = () => { if (quantity > 1) setQuantity(prev => prev - 1); };

  const calculateDiscount = () => {
    if (!product || !product.price || !product.after_discount_price) return 0;
    const price = parseFloat(product.price);
    const discounted = parseFloat(product.after_discount_price);
    if(price === discounted) return 0;
    return Math.round(100 - (discounted / price) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!product) return <div>Product not found</div>;

  const discount = calculateDiscount();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-gray-600 mb-6">
          <button onClick={() => navigate('/')} className="hover:text-blue-600">Home</button>
          <span className="mx-2">/</span>
          <button onClick={() => navigate('/product')} className="hover:text-blue-600">Products</button>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium truncate">{product.product_name}</span>
        </nav>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
            
            {/* Left Column: Images */}
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                <img
                  src={selectedImage || "https://via.placeholder.com/400"}
                  alt={product.product_name}
                  className="w-full h-96 object-contain p-4 mix-blend-multiply"
                />
                {discount > 0 && (
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {discount}% OFF
                  </div>
                )}
              </div>

              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(img)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        selectedImage === img ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                      }`}
                    >
                      <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Details */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {product.product_name}
                </h1>
                
                {product.sub_title && (
                  <p className="text-lg text-gray-500 font-medium mb-3">
                    {product.sub_title}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {product.product_code || 'General'}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <div className="flex text-yellow-500">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < Math.floor(product.rating || 0) ? "" : "text-gray-300"} />
                    ))}
                  </div>
                  <span className="text-gray-600 text-sm">
                    ({product.rating || 0} Ratings)
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ₹{product.after_discount_price}
                  </span>
                  
                  {parseFloat(product.price) > parseFloat(product.after_discount_price) && (
                    <>
                      <span className="text-xl text-gray-500 line-through">
                        ₹{product.price}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-gray-500 text-sm mt-1">Inclusive of all taxes</p>
              </div>

              {/* Quantity */}
              <div className="pb-6 border-b">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Quantity</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button onClick={decreaseQuantity} className="px-4 py-2 hover:bg-gray-100 transition disabled:opacity-50" disabled={quantity <= 1 || isInCart}>−</button>
                    <span className="px-4 py-2 font-semibold min-w-[50px] text-center">{quantity}</span>
                    <button onClick={increaseQuantity} className="px-4 py-2 hover:bg-gray-100 transition" disabled={isInCart}>+</button>
                  </div>
                  {isInCart && (
                     <span className="text-sm text-blue-600 font-medium">Item already in cart</span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                
                {/* Dynamic Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 font-semibold py-4 px-6 rounded-xl transition duration-300 flex items-center justify-center gap-3 shadow-md
                    ${isInCart 
                      ? 'bg-green-600 text-white hover:bg-green-700' // Green if in cart
                      : 'bg-blue-600 text-white hover:bg-blue-700'   // Blue if not
                    }`}
                >
                  {isInCart ? (
                    <>
                      <FaCheck className="w-5 h-5" />
                      Added to Cart
                    </>
                  ) : (
                    <>
                      <FaShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
                
                {/* Buy Now / Go to Cart Button */}
                <button
                  onClick={handleBuyNow}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-4 px-6 rounded-xl transition duration-300 flex items-center justify-center gap-3 shadow-md"
                >
                  {isInCart ? (
                    <>
                    Go to Cart <FaArrowRight />
                    </>
                  ) : "Buy Now"}
                </button>
              </div>

              {/* Features Icons */}
              <div className="grid grid-cols-4 gap-2 pt-4">
                <div className="flex flex-col items-center text-center p-2">
                  <FaTruck className="text-2xl text-blue-500 mb-1" />
                  <span className="text-[10px] text-gray-500">Fast Delivery</span>
                </div>
                <div className="flex flex-col items-center text-center p-2">
                  <FaUndo className="text-2xl text-blue-500 mb-1" />
                  <span className="text-[10px] text-gray-500">Easy Returns</span>
                </div>
                <div className="flex flex-col items-center text-center p-2">
                  <FaShieldAlt className="text-2xl text-blue-500 mb-1" />
                  <span className="text-[10px] text-gray-500">Secure Pay</span>
                </div>
                <div className="flex flex-col items-center text-center p-2">
                  <FaStar className="text-2xl text-blue-500 mb-1" />
                  <span className="text-[10px] text-gray-500">Top Quality</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description & Specifications */}
          <div className="bg-gray-50 px-6 md:px-8 py-8 border-t">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Product Description</h2>
            <div className="prose max-w-none text-gray-700 whitespace-pre-line leading-relaxed">
              {product.product_description}
            </div>
            
            {product.specifications && (
               <div className="mt-6">
                 <h2 className="text-xl font-bold text-gray-900 mb-4">Specifications</h2>
                 <div className="prose max-w-none text-gray-700 whitespace-pre-line leading-relaxed">
                   {product.specifications}
                 </div>
               </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductCart;